export interface ApiConfig {
  baseUrl: string;
  wsUrl: string;
  timeout: number;
  retryCount: number;
  retryDelay: number;
  version: string;
  env: 'development' | 'production' | 'local';
}

// window.env 타입 선언 (CAREFORU_* 환경변수)
declare global {
  interface Window {
    env?: {
      CAREFORU_API_BASE_URL?: string;
      CAREFORU_API_TIMEOUT?: string;
      CAREFORU_API_RETRY_COUNT?: string;
      CAREFORU_API_RETRY_DELAY?: string;
      CAREFORU_VERSION?: string;
      CAREFORU_FEATURE_FLAG?: string;
      CAREFORU_APP_TARGET?: string;
    };
  }
}

// 환경별 기본 설정
const envConfigs: Record<string, Partial<ApiConfig>> = {
  development: {
    baseUrl: 'http://careforu.iptime.org:8000',
    wsUrl: 'ws://careforu.iptime.org:8000/ws',
    env: 'development',
  },
  production: {
    baseUrl: 'https://api.tailody.com',
    wsUrl: 'wss://api.tailody.com/ws',
    env: 'production',
  },
  local: {
    baseUrl: 'http://localhost:8080',
    wsUrl: 'ws://localhost:8080/ws',
    env: 'local',
  },
};

// 기본 설정 (fallback)
export const defaultConfig: ApiConfig = {
  baseUrl: 'http://localhost:8080',
  wsUrl: 'ws://localhost:8080/ws',
  timeout: 30000,
  retryCount: 1,
  retryDelay: 1000,
  version: '1.0.0',
  env: 'development',
};

// 현재 설정
let currentConfig: ApiConfig = { ...defaultConfig };
let isConfigLoaded = false;

/**
 * 런타임 환경변수 가져오기 (window.env 우선)
 * Docker 컨테이너에서 주입된 환경변수를 우선 사용
 */
const getRuntimeEnv = (key: string): string | undefined => {
  // 1순위: window.env (Docker 런타임 주입 - CAREFORU_* 변수)
  if (typeof window !== 'undefined' && window.env) {
    const value = window.env[key as keyof typeof window.env];
    // 빈 문자열이나 템플릿 변수(${...})는 무시
    if (value && value !== '' && !value.startsWith('${')) {
      return value;
    }
  }

  // 2순위: import.meta.env (Vite 빌드타임 - VITE_* 변수)
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    // CAREFORU_* 키를 VITE_* 키로 매핑
    const viteKeyMap: Record<string, string> = {
      CAREFORU_API_BASE_URL: 'VITE_API_BASE_URL',
      CAREFORU_API_TIMEOUT: 'VITE_API_TIMEOUT',
      CAREFORU_API_RETRY_COUNT: 'VITE_API_RETRY_COUNT',
      CAREFORU_API_RETRY_DELAY: 'VITE_API_RETRY_DELAY',
      CAREFORU_VERSION: 'VITE_VERSION',
      CAREFORU_FEATURE_FLAG: 'VITE_FEATURE_FLAG',
    };
    const viteKey = viteKeyMap[key] || key;
    const value = import.meta.env[viteKey];
    if (value && value !== '') {
      return value;
    }
  }

  return undefined;
};

/**
 * 현재 환경 감지
 */
const detectEnvironment = (): string => {
  // window.env에서 baseUrl을 기반으로 환경 감지
  if (typeof window !== 'undefined' && window.env?.CAREFORU_API_BASE_URL) {
    const baseUrl = window.env.CAREFORU_API_BASE_URL;
    if (baseUrl.includes('localhost')) return 'local';
    if (baseUrl.includes('api.tailody.com')) return 'production';
    return 'development';
  }

  // Vite MODE fallback
  if (typeof import.meta !== 'undefined' && import.meta.env?.MODE) {
    return import.meta.env.MODE;
  }

  return 'development';
};

/**
 * WebSocket URL 생성 (baseUrl 기반)
 */
const generateWsUrl = (baseUrl: string): string => {
  try {
    const url = new URL(baseUrl);
    const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${url.host}/ws`;
  } catch {
    return 'ws://localhost:8080/ws';
  }
};

/**
 * 런타임 환경변수에서 API 설정을 로드
 */
export const loadConfigFromEnv = (): void => {
  if (isConfigLoaded) {
    return;
  }

  const currentEnv = detectEnvironment();
  const envConfig = envConfigs[currentEnv] || envConfigs.development;

  // 환경변수에서 설정 로드 (window.env > import.meta.env > 기본값)
  const baseUrl = getRuntimeEnv('CAREFORU_API_BASE_URL') || envConfig.baseUrl || defaultConfig.baseUrl;

  currentConfig = {
    baseUrl,
    wsUrl: generateWsUrl(baseUrl),
    timeout: Number(getRuntimeEnv('CAREFORU_API_TIMEOUT')) || defaultConfig.timeout,
    retryCount: Number(getRuntimeEnv('CAREFORU_API_RETRY_COUNT')) || defaultConfig.retryCount,
    retryDelay: Number(getRuntimeEnv('CAREFORU_API_RETRY_DELAY')) || defaultConfig.retryDelay,
    version: getRuntimeEnv('CAREFORU_VERSION') || defaultConfig.version,
    env: (currentEnv as ApiConfig['env']) || defaultConfig.env,
  };

  const source = typeof window !== 'undefined' && window.env?.CAREFORU_API_BASE_URL
    ? 'window.env (runtime)'
    : 'import.meta.env (build-time)';

  console.log(`[API Config] Environment: ${currentEnv}`);
  console.log(`[API Config] Source: ${source}`);
  console.log('[API Config] Loaded:', currentConfig);
  isConfigLoaded = true;
};

/**
 * API 설정 가져오기
 */
export const getConfig = (): ApiConfig => {
  if (!isConfigLoaded) {
    loadConfigFromEnv();
  }
  return currentConfig;
};

/**
 * 현재 환경 가져오기
 */
export const getEnvironment = (): string => {
  if (!isConfigLoaded) {
    loadConfigFromEnv();
  }
  return currentConfig.env;
};

/**
 * 개발 환경인지 확인
 */
export const isDevelopment = (): boolean => {
  return getEnvironment() === 'development' || getEnvironment() === 'local';
};

/**
 * 프로덕션 환경인지 확인
 */
export const isProduction = (): boolean => {
  return getEnvironment() === 'production';
};

/**
 * API 설정 수동 업데이트 (테스트용)
 */
export const updateConfig = (config: Partial<ApiConfig>): void => {
  currentConfig = { ...currentConfig, ...config };
  console.log('[API Config] Manually updated:', currentConfig);
};

/**
 * 버전 정보 가져오기
 */
export const getVersion = (): string => {
  if (!isConfigLoaded) {
    loadConfigFromEnv();
  }
  return currentConfig.version;
};
